import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ApiEndpoints } from '@avisoma/shared';
import backend from '../services/backend';

export type GoResult = ApiEndpoints<string>['/api/go']['POST']['response']['results'][number];

export function pathForResult(result: GoResult): string {
  switch (result.type) {
    case 'document':
      return `/documents/${result.id}`;
    case 'page':
      return `/document-pages/${result.id}`;
    case 'project':
      return `/projects/${result.id}`;
    case 'organisation':
      return `/organisations/${result.id}`;
    case 'reconstructed-document':
      return `/reconstructed-documents/${result.id}`;
  }
}

export type UseGoQueryOptions = {
  onNavigate?: () => void;
};

export function useGoQuery(options?: UseGoQueryOptions) {
  const navigate = useNavigate();
  const [noResult, setNoResult] = useState(false);
  const [results, setResults] = useState<GoResult[]>([]);

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  // Per-submit generation counter. Each submit() bumps it and tags the
  // mutation vars; onSuccess/onError check the tag against the current value
  // and bail if stale. reset({ cancelPending: true }) bumps it too — that's
  // how the dialog cancels navigation when the user dismisses mid-flight.
  const requestIdRef = useRef(0);

  const goTo = (result: GoResult, openInNewTab: boolean) => {
    const path = pathForResult(result);
    if (openInNewTab) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    optionsRef.current?.onNavigate?.();
  };

  const mutation = useMutation({
    mutationFn: (vars: { query: string; openInNewTab: boolean; requestId: number }) =>
      backend.go(vars.query),
    onSuccess: (res, vars) => {
      if (vars.requestId !== requestIdRef.current) return;
      const found = res.data.results;
      if (found.length === 0) {
        setNoResult(true);
        return;
      }
      if (found.length === 1) {
        goTo(found[0], vars.openInNewTab);
        return;
      }
      setResults(found);
    },
    onError: (_err, vars) => {
      // Stale errors are ignored. We can't reset() here — useMutation holds a
      // single state slot, so resetting would also wipe a newer in-flight call's
      // spinner. Dialog dismissal handles leftover state via reset({cancelPending}).
      if (vars.requestId !== requestIdRef.current) return;
    },
  });

  const submit = (query: string, openInNewTab: boolean) => {
    setNoResult(false);
    setResults([]);
    const requestId = ++requestIdRef.current;
    mutation.mutate({ query, openInNewTab, requestId });
  };

  const pick = (result: GoResult, openInNewTab: boolean) => goTo(result, openInNewTab);

  const reset = (opts?: { cancelPending?: boolean }) => {
    // Reset clears the no-result flag and any stale picker results, and bumps
    // the generation counter so an in-flight response for an abandoned query
    // can neither auto-navigate nor refill the picker. Per-keystroke callers
    // rely on this when the user edits the query mid-flight; the mutation
    // itself is left alone so its spinner isn't hidden.
    setNoResult(false);
    setResults([]);
    requestIdRef.current++;
    if (opts?.cancelPending) {
      // Consumer (e.g. dialog close) additionally resets the mutation to clear
      // any surfaced isPending / isError carried over from the previous
      // attempt so the next open starts clean.
      mutation.reset();
    }
  };

  return {
    submit,
    pick,
    reset,
    results,
    isPending: mutation.isPending,
    isError: mutation.isError,
    noResult,
  };
}
